// frontend/src/components/ArtistSummary.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getArtistData } from '../state/ArtistManager';
import { getCatalogAnalytics } from '../services/AnalyticsService';
import './ArtistSummary.css';

const ArtistSummary = () => {
  const { user, isAuthenticated } = useAuth();
  const [artistData, setArtistData] = useState(null);
  const [catalogData, setCatalogData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchArtistSummary = async () => {
      if (!isAuthenticated || !user?.artistId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch artist data from ArtistManager
        const artistResponse = await getArtistData(user.artistId);
        setArtistData(artistResponse.artist);

        // Fetch catalog analytics
        const catalogResponse = await getCatalogAnalytics(user.artistId);
        setCatalogData(catalogResponse);
        
      } catch (error) {
        console.error('Error fetching artist summary:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArtistSummary();
  }, [isAuthenticated, user?.artistId]);

  if (!isAuthenticated || loading) {
    return null;
  }

  if (!artistData) {
    return (
      <div className="artist-summary error">
        <span>⚠️ Artist data unavailable</span>
      </div>
    );
  }

  return (
    <div className={`artist-summary ${expanded ? 'expanded' : 'collapsed'}`}>
      <div className="summary-header" onClick={() => setExpanded(!expanded)}>
        <div className="artist-info">
          <div className="artist-id">{artistData.id.toUpperCase()}</div>
          <div className="artist-name">{artistData.name}</div>
        </div>
        <div className="expand-icon">
          {expanded ? '▼' : '▶'}
        </div>
      </div>
      
      {expanded && (
        <div className="summary-details">
          <div className="artist-details">
            <h4>Artist Profile</h4>
            <div className="detail-item">
              <span className="label">DecodedMusic ID:</span>
              <span className="value">{artistData.id}</span>
            </div>
            <div className="detail-item">
              <span className="label">Artist Name:</span>
              <span className="value">{artistData.name}</span>
            </div>
            <div className="detail-item">
              <span className="label">Email:</span>
              <span className="value">{artistData.email}</span>
            </div>
            {artistData.spotifyId && (
              <div className="detail-item">
                <span className="label">Spotify ID:</span>
                <span className="value">{artistData.spotifyId}</span>
              </div>
            )}
            {artistData.ascapId && (
              <div className="detail-item">
                <span className="label">ASCAP ID:</span>
                <span className="value">{artistData.ascapId}</span>
              </div>
            )}
            <div className="detail-item">
              <span className="label">Status:</span>
              <span className={`value status ${artistData.status}`}>
                {artistData.status.toUpperCase()}
              </span>
            </div>
          </div>

          {catalogData && (
            <div className="catalog-summary">
              <h4>Catalog Overview</h4>
              <div className="metrics-grid">
                <div className="metric">
                  <div className="metric-value">{catalogData.metrics?.totalTracks || 0}</div>
                  <div className="metric-label">Total Tracks</div>
                </div>
                <div className="metric">
                  <div className="metric-value">{catalogData.metrics?.marketingReady || 0}</div>
                  <div className="metric-label">Marketing Ready</div>
                </div>
                <div className="metric">
                  <div className="metric-value">{catalogData.metrics?.avgViralScore?.toFixed(1) || '0.0'}</div>
                  <div className="metric-label">Avg Viral Score</div>
                </div>
                <div className="metric">
                  <div className="metric-value">{catalogData.insights?.highViralPotential || 0}</div>
                  <div className="metric-label">High Potential</div>
                </div>
              </div>
            </div>
          )}

          <div className="last-updated">
            <small>
              Last updated: {artistData.catalogSummary?.lastUpdated ? 
                new Date(artistData.catalogSummary.lastUpdated).toLocaleDateString() : 
                'N/A'
              }
            </small>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtistSummary;
